import { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'
import { FAQS } from './data/wp-structured'
import type { FAQ } from './data/wp-structured'

const CATEGORIES = ['Submission', 'Review', 'Plagiarism & AI', 'UGC-CARE', 'Publication Fees', 'General Info'] as const
type Category = (typeof CATEGORIES)[number]

function categorize(f: FAQ): Category {
    const t = `${f.q} ${f.a}`.toLowerCase()
    if (/plagiar|similar|turnitin|\bai\b|artificial intelligence|originality/.test(t)) return 'Plagiarism & AI'
    if (/ugc|care list|index|scopus|doaj|web of science/.test(t)) return 'UGC-CARE'
    if (/fee|charge|\bapc\b|payment|subscription|cost|price|₹|rs\.?\s*\d/.test(t)) return 'Publication Fees'
    if (/review|referee|peer|\bsla\b|decision|revision|accept|reject/.test(t)) return 'Review'
    if (/submit|submission|manuscript|upload|format|template|word|abstract|keyword|cover letter/.test(t)) return 'Submission'
    return 'General Info'
}

export default function FAQs() {
    const [open, setOpen] = useState<string | null>(FAQS[0]?.q ?? null)
    const [query, setQuery] = useState('')
    const [activeCat, setActiveCat] = useState<Category | 'all'>('all')

    const tagged = useMemo(() => FAQS.map((f) => ({ ...f, cat: categorize(f) })), [])

    const counts = useMemo(() => {
        const c: Record<string, number> = { all: tagged.length }
        for (const cat of CATEGORIES) c[cat] = 0
        tagged.forEach((f) => { c[f.cat] += 1 })
        return c
    }, [tagged])

    const q = query.trim().toLowerCase()
    const filtered = tagged.filter((f) => {
        if (activeCat !== 'all' && f.cat !== activeCat) return false
        if (!q) return true
        return f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)
    })

    return (
        <>
            <SEO
                title="FAQs"
                description={`${FAQS.length} questions on submission, peer review, plagiarism, fees, and editorial policy for Indian Studies Review.`}
                path="/faqs"
            />
            <PageHeader
                eyebrow="Frequently Asked Questions"
                title="Answers, on the record"
                lede="Submission, peer review, plagiarism, indexing, fees, and the everyday questions authors and reviewers ask the editorial office."
            />

            <section className="section">
                <div className="container-prose max-w-3xl mx-auto">
                    <div className="mb-6">
                        <input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search the FAQ…"
                            className="w-full px-4 py-3 text-[15px] bg-white"
                            style={{ border: '1px solid var(--color-rule-strong)', color: 'var(--color-ink-700)', borderRadius: '2px' }}
                            aria-label="Search FAQ"
                        />
                    </div>

                    <div className="flex flex-wrap gap-2 mb-3">
                        {(['all', ...CATEGORIES] as const).map((cat) => {
                            const active = activeCat === cat
                            const count = counts[cat] ?? 0
                            if (cat !== 'all' && count === 0) return null
                            return (
                                <button
                                    key={cat}
                                    onClick={() => { setActiveCat(cat); setOpen(null) }}
                                    className="inline-flex items-center gap-2 px-3.5 py-2 text-[11px] uppercase font-semibold transition-colors"
                                    style={{
                                        letterSpacing: '0.14em',
                                        color: active ? 'var(--color-paper-100)' : 'var(--color-ink-500)',
                                        background: active ? 'var(--color-gold-700)' : 'transparent',
                                        border: `1px solid ${active ? 'var(--color-gold-700)' : 'var(--color-rule-strong)'}`
                                    }}
                                >
                                    {cat === 'all' ? 'All' : cat}
                                    <span
                                        className="inline-flex items-center justify-center text-[10px] font-bold"
                                        style={{
                                            minWidth: '1.25rem',
                                            height: '1.25rem',
                                            padding: '0 0.3rem',
                                            borderRadius: '999px',
                                            background: active ? 'var(--color-gold-500)' : 'var(--color-paper-200)',
                                            color: active ? 'var(--color-paper-50)' : 'var(--color-ink-500)'
                                        }}
                                    >
                                        {count}
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                    <div className="mb-8 text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.18em', color: 'var(--color-ink-400)' }}>
                        {filtered.length} {filtered.length === 1 ? 'question' : 'questions'}
                    </div>

                    <ul style={{ borderTop: '1px solid var(--color-rule-strong)' }}>
                        {filtered.map((f) => {
                            const isOpen = open === f.q
                            return (
                                <motion.li
                                    key={f.q}
                                    initial={{ opacity: 0, y: 6 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.3 }}
                                    style={{ borderBottom: '1px solid var(--color-rule)' }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setOpen(isOpen ? null : f.q)}
                                        aria-expanded={isOpen}
                                        className="w-full text-left py-5 grid grid-cols-[1fr_auto] gap-4 items-baseline"
                                    >
                                        <span className="flex flex-col gap-1.5">
                                            <span className="text-[9.5px] uppercase font-bold" style={{ letterSpacing: '0.2em', color: 'var(--color-gold-700)' }}>
                                                {f.cat}
                                            </span>
                                            <span
                                                className="font-display"
                                                style={{ fontSize: 'clamp(1rem, 1.5vw, 1.15rem)', color: 'var(--color-ink-700)', letterSpacing: '-0.012em', lineHeight: 1.3, fontWeight: 500 }}
                                            >
                                                {f.q}
                                            </span>
                                        </span>
                                        <span
                                            aria-hidden
                                            className="text-[12px] font-semibold"
                                            style={{ color: 'var(--color-gold-700)', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.18s ease' }}
                                        >
                                            ▾
                                        </span>
                                    </button>

                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                key="content"
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.25, ease: 'easeOut' }}
                                                style={{ overflow: 'hidden' }}
                                            >
                                                <div
                                                    className="pb-6 pr-8 wp-prose"
                                                    style={{ maxWidth: 'none', fontSize: '15px' }}
                                                    dangerouslySetInnerHTML={{ __html: f.a }}
                                                />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.li>
                            )
                        })}
                    </ul>

                    {filtered.length === 0 && (
                        <div className="text-center py-16" style={{ color: 'var(--color-ink-400)' }}>
                            No FAQ matches your filter. Try a different keyword or category.
                        </div>
                    )}
                </div>
            </section>
        </>
    )
}
